import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getScoreColors } from '../utils/colorUtils';
import { ScrollManager } from '../utils/scrollManager';
import MarkdownRenderer from '../components/MarkdownRenderer';
import MatrixVisualization, { MatrixVisualizationHandle } from '../components/MatrixVisualization';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  DocumentArrowDownIcon,
  ChartBarIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  RocketLaunchIcon,
  CalendarDaysIcon,
  TagIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

interface Problem {
  id: string;
  title: string;
  description: string;
  impact_area: string;
  priority: number;
  project_id?: string;
  stakeholders?: Array<{ name: string; role: string }>;
  metrics?: Array<{
    name: string;
    description: string;
    current_value?: number;
    target_value?: number;
    unit?: string;
  }>;
}

interface Hypothesis {
  id: string;
  title: string;
  hypothesis_statement: string;
  business_impact: number;
  technical_feasibility: number;
  strategy_alignment: number;
  implementation_effort: number;
  priority_score: number;
  problem_id: string;
  project_id?: string;
  description: string;
  ai_capability: string;
  expected_outcome: string;
  is_active?: boolean;
}

interface Project {
  id: string;
  name: string;
  description: string;
  status: string;
  created_at: string;
}



const ReportsPage: React.FC = () => {
  const { get } = useApi();
  useWebSocket();
  const reportRef = useRef<HTMLDivElement>(null);
  const mainMatrixRef = useRef<MatrixVisualizationHandle>(null);
  const problemMatrixRefs = useRef<Map<string, MatrixVisualizationHandle>>(new Map());
  
  const [problems, setProblems] = useState<Problem[]>([]);
  const [hypotheses, setHypotheses] = useState<Hypothesis[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingHtml, setExportingHtml] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>('');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsData, problemsData, hypothesesData] = await Promise.all([
          get<Project[]>('/projects'),
          get<Problem[]>('/problems'),
          get<Hypothesis[]>('/hypotheses')
        ]);

        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setProblems(Array.isArray(problemsData) ? problemsData : []);
        setHypotheses(Array.isArray(hypothesesData) ? hypothesesData : []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load report data. Please ensure the FastAPI server is running.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [get]);

  // Handle scroll restoration after edit
  useEffect(() => {
    if (!loading && ScrollManager.hasPendingReturn()) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        ScrollManager.restorePosition();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading]);

  // Filter data based on selected project
  const filteredProjects = selectedProjectFilter 
    ? projects.filter(p => p.id === selectedProjectFilter)
    : projects;
    
  const filteredProblems = selectedProjectFilter 
    ? problems.filter(p => p.project_id === selectedProjectFilter)
    : problems;
    
  const filteredHypotheses = selectedProjectFilter 
    ? hypotheses.filter(h => h.project_id === selectedProjectFilter)
    : hypotheses;

  // Calculate analytics based on filtered data
  const analytics = {
    totalProjects: filteredProjects.length,
    totalProblems: filteredProblems.length,
    totalHypotheses: filteredHypotheses.length,
    avgPriorityScore: filteredHypotheses.length > 0 
      ? filteredHypotheses.reduce((sum, h) => sum + (h.priority_score || 0), 0) / filteredHypotheses.length 
      : 0,
    highPriorityHypotheses: filteredHypotheses.filter(h => (h.priority_score || 0) >= 7).length,
    problemsWithoutHypotheses: filteredProblems.filter(p => !filteredHypotheses.some(h => h.problem_id === p.id)).length,
    topProblemAreas: Array.from(new Set(filteredProblems.map(p => p.impact_area))).slice(0, 5),
    aiCapabilities: Array.from(new Set(filteredHypotheses.map(h => h.ai_capability))).filter(Boolean).slice(0, 5)
  };

  // Export to PDF function
  const exportToPDF = async () => {
    if (!reportRef.current) return;

    try {
      setExporting(true);
      
      // Create canvas from the report content
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight
      });

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      let position = 0;

      // Add the image to PDF
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      const fileName = `LeanAI_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };


  // Export to HTML function using Vega native export
  const exportToHTML = async () => {
    if (!reportRef.current) return;

    try {
      setExportingHtml(true);
      
      // Export all matrix visualizations using Vega's native export
      const matrixExports: { [key: string]: string } = {};
      
      // Export main matrix
      if (mainMatrixRef.current) {
        try {
          matrixExports['main-matrix'] = await mainMatrixRef.current.exportToSVG();
        } catch (error) {
          console.warn('Could not export main matrix:', error);
        }
      }
      
      // Export problem-specific matrices
      const problemMatrixEntries = Array.from(problemMatrixRefs.current.entries());
      for (const [problemId, matrixRef] of problemMatrixEntries) {
        try {
          matrixExports[`problem-matrix-${problemId}`] = await matrixRef.exportToSVG();
        } catch (error) {
          console.warn(`Could not export matrix for problem ${problemId}:`, error);
        }
      }
      
      // Clone the report content
      const clonedContent = reportRef.current.cloneNode(true) as HTMLElement;
      
      // Remove any script tags for security
      const scripts = clonedContent.querySelectorAll('script');
      scripts.forEach(script => script.remove());

      // Replace Vega chart containers with exported SVGs
      const vegaContainers = clonedContent.querySelectorAll('.vega-embed');
      let mainMatrixReplaced = false;
      
      vegaContainers.forEach((container, _index) => {
        const parent = container.parentElement;
        if (parent) {
          parent.classList.add('matrix-container');
          
          // Determine which exported SVG to use
          let svgContent = '';
          
          // Check if this is the main matrix (first one) or a problem-specific matrix
          if (!mainMatrixReplaced && matrixExports['main-matrix']) {
            svgContent = matrixExports['main-matrix'] || '';
            mainMatrixReplaced = true;
          } else {
            // Find the corresponding problem matrix
            // Look for problem ID in parent elements
            let currentElement = parent.parentElement;
            let problemId = '';
            
            while (currentElement && !problemId) {
              if (currentElement.id && currentElement.id.startsWith('problem-')) {
                problemId = currentElement.id.replace('problem-', '');
                break;
              }
              currentElement = currentElement.parentElement;
            }
            
            if (problemId && matrixExports[`problem-matrix-${problemId}`]) {
              svgContent = matrixExports[`problem-matrix-${problemId}`] || '';
            }
          }
          
          if (svgContent) {
            // Create a div to hold the SVG
            const svgContainer = document.createElement('div');
            svgContainer.className = 'exported-matrix';
            svgContainer.innerHTML = svgContent;
            parent.replaceChild(svgContainer, container);
          } else {
            // Fallback content
            const fallbackDiv = document.createElement('div');
            fallbackDiv.className = 'matrix-fallback';
            fallbackDiv.innerHTML = `
              <div>
                <h4 style="margin: 0 0 12px 0; font-weight: 600;">Matrix Visualization</h4>
                <p style="margin: 0; font-size: 14px;">Interactive chart - view in the live application for full functionality</p>
              </div>
            `;
            parent.replaceChild(fallbackDiv, container);
          }
        }
      });

      // Get all stylesheets from the document
      const getPageStyles = (): string => {
        let styles = '';
        
        // Get styles from all stylesheets
        for (let i = 0; i < document.styleSheets.length; i++) {
          try {
            const styleSheet = document.styleSheets[i];
            if (styleSheet && styleSheet.cssRules) {
              for (let j = 0; j < styleSheet.cssRules.length; j++) {
                const rule = styleSheet.cssRules[j];
                if (rule) {
                  styles += rule.cssText + '\n';
                }
              }
            }
          } catch (e) {
            // Skip cross-origin stylesheets
            console.warn('Cannot access stylesheet:', e);
          }
        }
        
        // Get inline styles from style tags
        const styleTags = document.querySelectorAll('style');
        styleTags.forEach(styleTag => {
          styles += styleTag.textContent || '';
        });
        
        return styles;
      };

      // Create complete HTML document
      const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>LeanAI Report - ${new Date().toLocaleDateString()}</title>
    <style>
        /* Base styles */
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f9fafb;
            color: #111827;
            line-height: 1.6;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 0;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            overflow-x: hidden;
        }
        .export-note {
            background: #f3f4f6;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            padding: 12px;
            margin: 20px 0;
            font-size: 14px;
            color: #6b7280;
        }
        
        /* Tailwind CSS essentials */
        .p-6 { padding: 1.5rem; }
        .p-4 { padding: 1rem; }
        .p-3 { padding: 0.75rem; }
        .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
        .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
        .px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
        .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mb-4 { margin-bottom: 1rem; }
        .mb-6 { margin-bottom: 1.5rem; }
        .mb-8 { margin-bottom: 2rem; }
        .mr-2 { margin-right: 0.5rem; }
        .mr-3 { margin-right: 0.75rem; }
        .mr-4 { margin-right: 1rem; }
        .ml-4 { margin-left: 1rem; }
        .mt-4 { margin-top: 1rem; }
        .space-y-4 > * + * { margin-top: 1rem; }
        .space-y-6 > * + * { margin-top: 1.5rem; }
        .space-y-8 > * + * { margin-top: 2rem; }
        .space-y-12 > * + * { margin-top: 3rem; }
        .space-x-2 > * + * { margin-left: 0.5rem; }
        .space-x-3 > * + * { margin-left: 0.75rem; }
        .space-x-4 > * + * { margin-left: 1rem; }
        
        .bg-white { background-color: #fff; }
        .bg-gray-50 { background-color: #f9fafb; }
        .bg-gray-100 { background-color: #f3f4f6; }
        .bg-blue-100 { background-color: #dbeafe; }
        .bg-green-100 { background-color: #dcfce7; }
        .bg-yellow-100 { background-color: #fef3c7; }
        .bg-orange-100 { background-color: #fed7aa; }
        .bg-red-50 { background-color: #fef2f2; }
        .bg-orange-50 { background-color: #fff7ed; }
        .bg-indigo-50 { background-color: #eef2ff; }
        .bg-purple-50 { background-color: #faf5ff; }
        .bg-gradient-to-r { background-image: linear-gradient(to right, var(--tw-gradient-stops)); }
        .from-indigo-50 { --tw-gradient-from: #eef2ff; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(238, 242, 255, 0)); }
        .to-purple-50 { --tw-gradient-to: #faf5ff; }
        .from-orange-50 { --tw-gradient-from: #fff7ed; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(255, 247, 237, 0)); }
        .to-red-50 { --tw-gradient-to: #fef2f2; }
        
        .text-gray-900 { color: #111827; }
        .text-gray-700 { color: #374151; }
        .text-gray-600 { color: #4b5563; }
        .text-gray-500 { color: #6b7280; }
        .text-gray-400 { color: #9ca3af; }
        .text-gray-300 { color: #d1d5db; }
        .text-indigo-600 { color: #4f46e5; }
        .text-blue-600 { color: #2563eb; }
        .text-green-600 { color: #16a34a; }
        .text-yellow-600 { color: #ca8a04; }
        .text-orange-600 { color: #ea580c; }
        .text-red-600 { color: #dc2626; }
        
        .text-xs { font-size: 0.75rem; line-height: 1rem; }
        .text-sm { font-size: 0.875rem; line-height: 1.25rem; }
        .text-base { font-size: 1rem; line-height: 1.5rem; }
        .text-lg { font-size: 1.125rem; line-height: 1.75rem; }
        .text-xl { font-size: 1.25rem; line-height: 1.75rem; }
        .text-2xl { font-size: 1.5rem; line-height: 2rem; }
        .text-3xl { font-size: 1.875rem; line-height: 2.25rem; }
        
        .font-medium { font-weight: 500; }
        .font-semibold { font-weight: 600; }
        .font-bold { font-weight: 700; }
        .italic { font-style: italic; }
        
        .border { border-width: 1px; border-color: #e5e7eb; }
        .border-2 { border-width: 2px; }
        .border-4 { border-width: 4px; }
        .border-t { border-top-width: 1px; border-color: #e5e7eb; }
        .border-b { border-bottom-width: 1px; border-color: #e5e7eb; }
        .border-l-4 { border-left-width: 4px; }
        .border-gray-200 { border-color: #e5e7eb; }
        .border-yellow-400 { border-color: #facc15; }
        
        .rounded-lg { border-radius: 0.5rem; }
        .rounded-full { border-radius: 9999px; }
        
        .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        
        .flex { display: flex; }
        .grid { display: grid; }
        .text-center { text-align: center; }
        .items-center { align-items: center; }
        .items-start { align-items: flex-start; }
        .justify-between { justify-content: space-between; }
        .justify-center { justify-content: center; }
        .flex-1 { flex: 1 1 0%; }
        
        .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
        .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .gap-4 { gap: 1rem; }
        .gap-6 { gap: 1.5rem; }
        
        .w-16 { width: 4rem; }
        .h-16 { height: 4rem; }
        .w-12 { width: 3rem; }
        .h-12 { height: 3rem; }
        .w-6 { width: 1.5rem; }
        .h-6 { height: 1.5rem; }
        .w-5 { width: 1.25rem; }
        .h-5 { height: 1.25rem; }
        .w-4 { width: 1rem; }
        .h-4 { height: 1rem; }
        .w-8 { width: 2rem; }
        .h-8 { height: 2rem; }
        .mx-auto { margin-left: auto; margin-right: auto; }
        .max-w-6xl { max-width: 72rem; }
        .w-full { width: 100%; }
        
        .leading-relaxed { line-height: 1.625; }
        .overflow-hidden { overflow: hidden; }
        
        /* Hero Icons - ensure proper sizing */
        svg {
            display: inline-block;
            vertical-align: middle;
        }
        
        /* Ensure SVG tooltips work */
        svg title {
            display: block;
        }
        
        /* Matrix visualization fixes for HTML export */
        .matrix-container {
            max-width: 100% !important;
            overflow: hidden;
            margin: 0 auto;
            box-sizing: border-box;
        }
        
        .matrix-container svg {
            max-width: 100% !important;
            width: 100% !important;
            height: auto !important;
            box-sizing: border-box;
        }
        
        /* Vega chart container fixes */
        .vega-embed {
            max-width: 100% !important;
            width: 100% !important;
            overflow: hidden;
            box-sizing: border-box;
        }
        
        .vega-embed .vega-actions {
            display: none !important;
        }
        
        .vega-embed details {
            display: none !important;
        }
        
        /* Fix chart positioning */
        [role="graphics-document"] {
            max-width: 100% !important;
            margin: 0 auto;
        }
        
        /* Container width constraints */
        .p-6.bg-white.rounded-lg.border.shadow-sm {
            box-sizing: border-box;
            overflow: hidden;
        }
        
        /* Legend color fixes */
        .legend-item {
            display: flex;
            align-items: center;
            margin-bottom: 4px;
        }
        
        .legend-color {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            margin-right: 8px;
            display: inline-block;
        }
        
        /* Matrix fallback styling */
        .matrix-fallback {
            text-align: center;
            padding: 40px 20px;
            border: 2px dashed #d1d5db;
            border-radius: 8px;
            background-color: #f9fafb;
            color: #6b7280;
        }
        
        /* Responsive design */
        @media (min-width: 768px) {
            .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .md\\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        }
        @media (min-width: 1024px) {
            .lg\\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        }
        
        ${getPageStyles()}
    </style>
</head>
<body>
    <div class="container">
        <div class="export-note">
            📄 This is a static HTML export of your LeanAI Report generated on ${new Date().toLocaleString()}.
            Hover over chart elements to see additional information.
        </div>
        ${clonedContent.outerHTML}
    </div>
</body>
</html>`;

      // Create and download the file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `LeanAI_Report_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating HTML:', error);
      alert('Failed to export HTML. Please try again.');
    } finally {
      setExportingHtml(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p className="font-medium">Error loading report data</p>
        <p className="mt-1 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header with Export Button */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AI Value Assessment Report
            {selectedProjectFilter && (
              <span className="block text-xl font-medium text-indigo-600 mt-1">
                {projects.find(p => p.id === selectedProjectFilter)?.name}
              </span>
            )}
          </h1>
          <p className="text-gray-600">
            Generated on {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            {selectedProjectFilter && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Project Filtered
              </span>
            )}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportToHTML}
            disabled={exportingHtml}
            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportingHtml ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                Export HTML
              </>
            )}
          </button>
          <button
            onClick={exportToPDF}
            disabled={exporting}
            className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                Export PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Project Filter */}
      <div className="bg-white shadow-sm rounded-lg p-4 mb-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label htmlFor="project-filter-reports" className="text-sm font-medium text-gray-700">
              Filter Report by Project:
            </label>
            <select
              id="project-filter-reports"
              value={selectedProjectFilter}
              onChange={(e) => setSelectedProjectFilter(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm min-w-48"
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          
          {selectedProjectFilter && (
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                {projects.find(p => p.id === selectedProjectFilter)?.name}
              </span>
              <button
                onClick={() => setSelectedProjectFilter('')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Content */}
      <div ref={reportRef} className="bg-white space-y-8">
        
        {/* 1. PROJECT DETAILS SECTION */}
        <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <RocketLaunchIcon className="h-6 w-6 mr-2 text-indigo-600" />
            Project Portfolio Overview
          </h2>
          
          {/* Executive Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              {
                title: 'Active Projects',
                value: analytics.totalProjects,
                icon: ChartBarIcon,
                color: 'text-blue-600',
                bgColor: 'bg-blue-100'
              },
              {
                title: 'Business Problems',
                value: analytics.totalProblems,
                icon: ExclamationTriangleIcon,
                color: 'text-orange-600',
                bgColor: 'bg-orange-100'
              },
              {
                title: 'AI Hypotheses',
                value: analytics.totalHypotheses,
                icon: LightBulbIcon,
                color: 'text-yellow-600',
                bgColor: 'bg-yellow-100'
              },
              {
                title: 'Avg Priority Score',
                value: analytics.avgPriorityScore.toFixed(1),
                icon: ArrowTrendingUpIcon,
                color: 'text-green-600',
                bgColor: 'bg-green-100'
              }
            ].map((metric, index) => (
              <div key={index} className="text-center">
                <div className={`w-16 h-16 ${metric.bgColor} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <metric.icon className={`h-8 w-8 ${metric.color}`} />
                </div>
                <div className="text-3xl font-bold text-gray-900">{metric.value}</div>
                <div className="text-sm text-gray-600 font-medium">{metric.title}</div>
              </div>
            ))}
          </div>

          {/* Detailed Project Information */}
          {filteredProjects.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedProjectFilter ? 'Selected Project' : 'Active Projects'}
              </h3>
              {filteredProjects.map((project) => {
                const projectAnchorId = ScrollManager.generateAnchorId('project', project.id);
                
                return (
                  <div key={project.id} id={projectAnchorId.slice(1)} className="bg-white p-6 rounded-lg border shadow-sm">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="text-xl font-bold text-gray-900">{project.name}</h4>
                          <button
                            onClick={() => ScrollManager.saveAndNavigate(
                              ScrollManager.generateEditUrl('project', project.id),
                              projectAnchorId
                            )}
                            className="ml-3 p-2 text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-lg transition-all duration-200"
                            title="Edit this project"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="text-gray-600 mb-3">
                          <MarkdownRenderer content={project.description} />
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <CalendarDaysIcon className="h-4 w-4 mr-1" />
                            Created: {new Date(project.created_at).toLocaleDateString()}
                          </div>
                          <div className="flex items-center">
                            <TagIcon className="h-4 w-4 mr-1" />
                            Status: {project.status}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          project.status === 'active' ? 'bg-green-100 text-green-800' :
                          project.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          project.status === 'on-hold' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                    </div>
                  
                  {/* Project-specific metrics */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-orange-600">
                        {filteredProblems.length}
                      </div>
                      <div className="text-xs text-gray-500">Problems Identified</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-yellow-600">
                        {filteredHypotheses.length}
                      </div>
                      <div className="text-xs text-gray-500">AI Solutions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">
                        {analytics.highPriorityHypotheses}
                      </div>
                      <div className="text-xs text-gray-500">High Priority</div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg border text-center">
              <p className="text-gray-500">No projects defined yet. Create your first project to begin the AI assessment process.</p>
            </div>
          )}
        </div>

        {/* 2. MATRIX VISUALIZATION SECTION */}
        <MatrixVisualization 
          ref={mainMatrixRef}
          hypotheses={filteredHypotheses} 
          problems={filteredProblems}
          showReferences={true}
          showQuadrantLabels={true}
          showLegend={true}
          width="container"
          height={500}
        />
        

        {/* 3. PROBLEMS AND HYPOTHESES SECTION */}
        <div className="p-6 bg-white rounded-lg border shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-orange-600" />
            Business Problems & AI Solutions
          </h2>
          
          {filteredProblems.length > 0 ? (
            <div className="space-y-12">
              {filteredProblems
                .sort((a, b) => (b.priority || 0) - (a.priority || 0))
                .map((problem, problemIndex) => {
                  const problemNumber = `P${problemIndex + 1}`;
                  const problemHypotheses = filteredHypotheses
                    .filter(h => h.problem_id === problem.id)
                    .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
                  
                  const problemAnchorId = ScrollManager.generateAnchorId('problem', problem.id);
                  
                  return (
                    <div key={problem.id} id={problemAnchorId.slice(1)} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                      {/* Problem Header */}
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 border-b">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <div className="flex items-center mr-4">
                                <span className="text-2xl font-bold text-gray-900">{problemNumber}</span>
                              </div>
                              <h3 className="text-xl font-bold text-gray-900">{problem.title}</h3>
                              <span className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${getScoreColors(problem.priority || 0).background} ${getScoreColors(problem.priority || 0).text}`}>
                                Priority: {problem.priority}/10
                              </span>
                              <button
                                onClick={() => ScrollManager.saveAndNavigate(
                                  ScrollManager.generateEditUrl('problem', problem.id),
                                  problemAnchorId
                                )}
                                className="ml-3 p-2 text-gray-600 hover:text-indigo-600 hover:bg-white/50 rounded-lg transition-all duration-200"
                                title="Edit this problem"
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                              </button>
                            </div>
                              <div className="text-gray-700 mb-4">
                                <MarkdownRenderer content={problem.description} />
                              </div>
                              
                              {/* Stakeholders and Metrics */}
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                {/* Stakeholders */}
                                {problem.stakeholders && problem.stakeholders.length > 0 && (
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Stakeholders</h5>
                                    <div className="space-y-1">
                                      {problem.stakeholders.map((stakeholder, index) => (
                                        <div key={index} className="bg-white border border-green-200 rounded px-2 py-1 text-xs">
                                          <span className="font-medium text-gray-900">{stakeholder.name}</span>
                                          <span className="text-gray-600"> - {stakeholder.role}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Metrics */}
                                {problem.metrics && problem.metrics.length > 0 && (
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Metrics</h5>
                                    <div className="space-y-1">
                                      {problem.metrics.map((metric, index) => (
                                        <div key={index} className="bg-white border border-blue-200 rounded px-2 py-1 text-xs">
                                          <div className="font-medium text-gray-900">{metric.name}</div>
                                          <div className="text-gray-600">{metric.description}</div>
                                          {(metric.current_value !== undefined || metric.target_value !== undefined) && (
                                            <div className="text-gray-500 text-xs">
                                              {metric.current_value !== undefined && `Current: ${metric.current_value}${metric.unit || ''}`}
                                              {metric.current_value !== undefined && metric.target_value !== undefined && ' → '}
                                              {metric.target_value !== undefined && `Target: ${metric.target_value}${metric.unit || ''}`}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-4 text-sm">
                                <span className="bg-white px-3 py-1 rounded-full text-gray-600 font-medium">
                                  Impact Area: {problem.impact_area}
                                </span>
                                <span className="bg-white px-3 py-1 rounded-full text-indigo-600 font-medium">
                                  {problemHypotheses.length} AI Solution{problemHypotheses.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Problem-Specific Matrix */}
                        {problemHypotheses.length > 0 && (
                          <div className="p-6 bg-gray-50 border-b">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                              <ChartBarIcon className="h-5 w-5 mr-2 text-indigo-600" />
                              Matrix View - {problemNumber} Focus
                            </h4>
                            <MatrixVisualization 
                              ref={(ref) => {
                                if (ref) {
                                  problemMatrixRefs.current.set(problem.id, ref);
                                } else {
                                  problemMatrixRefs.current.delete(problem.id);
                                }
                              }}
                              hypotheses={filteredHypotheses} 
                              problems={filteredProblems} 
                              focusProblemId={problem.id}
                              showReferences={true}
                              showQuadrantLabels={true}
                              showLegend={false}
                              width="container"
                              height={400}
                            />
                          </div>
                        )}

                        {/* Associated Hypotheses */}
                        <div className="p-6">
                          {problemHypotheses.length > 0 ? (
                            <div className="space-y-6">
                              <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                                <LightBulbIcon className="h-5 w-5 mr-2 text-yellow-500" />
                                AI Solution Hypotheses ({problemHypotheses.length})
                              </h4>
                              
                              {problemHypotheses.map((hypothesis, hypIndex) => {
                                const hypRef = `${problemNumber}.${hypIndex + 1}`;
                                const hypothesisAnchorId = ScrollManager.generateAnchorId('hypothesis', hypothesis.id);
                                
                                return (
                                  <div key={hypothesis.id} id={hypothesisAnchorId.slice(1)} className={`${hypothesis.is_active === true ? 'bg-green-50 border-l-4 border-green-500' : 'bg-gray-50 border-l-4 border-yellow-400'} p-6 rounded-lg`}>
                                    <div className="flex items-start justify-between mb-4">
                                      <div className="flex-1">
                                        <div className="flex items-center mb-2">
                                          <div className="flex items-center mr-3">
                                            <span className="text-lg font-bold text-gray-900">{hypRef}</span>
                                          </div>
                                          <h5 className="text-lg font-bold text-gray-900">{hypothesis.title}</h5>
                                          {hypothesis.is_active === true && (
                                            <span className="ml-3 px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                                              🔸 ACTIVE
                                            </span>
                                          )}
                                          <span className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${getScoreColors(hypothesis.priority_score || 0).background} ${getScoreColors(hypothesis.priority_score || 0).text}`}>
                                            Score: {hypothesis.priority_score?.toFixed(1) || 'N/A'}
                                          </span>
                                          <button
                                            onClick={() => ScrollManager.saveAndNavigate(
                                              ScrollManager.generateEditUrl('hypothesis', hypothesis.id),
                                              hypothesisAnchorId
                                            )}
                                            className="ml-3 p-2 text-gray-600 hover:text-indigo-600 hover:bg-white/50 rounded-lg transition-all duration-200"
                                            title="Edit this hypothesis"
                                          >
                                            <PencilSquareIcon className="h-4 w-4" />
                                          </button>
                                        </div>
                                      <div className="text-gray-700 italic mb-4 text-base leading-relaxed">
                                        <MarkdownRenderer content={`"${hypothesis.hypothesis_statement}"`} />
                                      </div>
                                      {hypothesis.description && (
                                        <div className="text-gray-700 mb-4 text-base leading-relaxed">
                                          <MarkdownRenderer content={hypothesis.description} />
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Detailed Metrics */}
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                                    <div className="text-center p-4 bg-white rounded-lg">
                                      <div className={`text-2xl font-bold ${getScoreColors(hypothesis.business_impact || 0).textOnly}`}>
                                        {hypothesis.business_impact}/10
                                      </div>
                                      <div className="text-sm text-gray-600 font-medium">Business Impact</div>
                                    </div>
                                    <div className="text-center p-4 bg-white rounded-lg">
                                      <div className={`text-2xl font-bold ${getScoreColors(hypothesis.technical_feasibility || 0).textOnly}`}>
                                        {hypothesis.technical_feasibility}/10
                                      </div>
                                      <div className="text-sm text-gray-600 font-medium">Technical Feasibility</div>
                                    </div>
                                    <div className="text-center p-4 bg-white rounded-lg">
                                      <div className={`text-2xl font-bold ${getScoreColors(hypothesis.strategy_alignment || 0).textOnly}`}>
                                        {hypothesis.strategy_alignment}/10
                                      </div>
                                      <div className="text-sm text-gray-600 font-medium">Strategy Alignment</div>
                                    </div>
                                    <div className="text-center p-4 bg-white rounded-lg">
                                      <div className={`text-2xl font-bold ${getScoreColors(hypothesis.implementation_effort || 0, true).textOnly}`}>
                                        {hypothesis.implementation_effort}/10
                                      </div>
                                      <div className="text-sm text-gray-600 font-medium">Implementation Effort</div>
                                    </div>
                                  </div>

                                  {/* Additional Details */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {hypothesis.ai_capability && (
                                      <div className="bg-white p-4 rounded-lg">
                                        <h6 className="font-semibold text-gray-900 mb-2">AI Capability</h6>
                                        <div className="text-gray-700 text-sm">
                                          <MarkdownRenderer content={hypothesis.ai_capability} />
                                        </div>
                                      </div>
                                    )}
                                    {hypothesis.expected_outcome && (
                                      <div className="bg-white p-4 rounded-lg">
                                        <h6 className="font-semibold text-gray-900 mb-2">Expected Outcome</h6>
                                        <div className="text-gray-700 text-sm">
                                          <MarkdownRenderer content={hypothesis.expected_outcome} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <LightBulbIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                              <p className="font-medium">No AI solutions defined for this problem</p>
                              <p className="text-sm">Consider developing AI hypotheses to address this business challenge.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <ExclamationTriangleIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No business problems defined yet</p>
              <p>Start by identifying key business challenges to assess AI opportunities.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-6 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <ClockIcon className="h-4 w-4" />
            <span>Report generated on {new Date().toLocaleString()}</span>
          </div>
          <p className="text-xs text-gray-400 mt-2">LeanAI - AI Value Assessment Platform</p>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;